import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  LOGIN_PATH,
  CANCER_DETECT_PATH,
  EXPERIENCE_PICKER_PATH,
} from '@/constants/paths';
import { getFullStoryPageName } from '@/utils/fullstoryPageNames';
import { useUserStore } from '@hooks/useUserStore';
import { ErrorLoadingPage } from '@/components/screens/ErrorHandling/ErrorLoadingPage';
import { monitorNetworkStatus } from '@/components/utils/utils';
import { parseQueryParams } from '@/utils/QueryParamsRoutes/queryParamsUtils';
import { useCancerDetectUser } from '@hooks/useCancerDetectUser';
import { useContextStore } from '@hooks/context/useContextStore';
import TokenManager from '@viome/viome-base/src/api/TokenManager';
import useLogout from '@hooks/useLogout';
import { useIframeModeStore } from '@hooks/useIframeModeStore';

export interface RouteProps {
  children: React.ReactNode;
  isProtected?: boolean;
  fromRegistration?: boolean;
}

export const Route = ({
  children,
  isProtected,
  fromRegistration,
}: RouteProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const logout = useLogout();
  const { isCancerDetectClicked } = useCancerDetectUser();
  const patientOrg = useContextStore((s) => s.patientOrg);
  const { user: userData } = useUserStore();
  const { isIframeMode } = useIframeModeStore();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [hasCheckedPath, setHasCheckedPath] = useState(false);

  useEffect(() => {
    const pageName = getFullStoryPageName(location.pathname);
    if (pageName) {
      window.FS?.setPage?.({ pageName });
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!hasCheckedPath) {
      const originalPath = `${window.location.pathname}${window.location.search}`;
      if (originalPath.includes('%3F') || originalPath.includes('%3f')) {
        const decodedPath = decodeURIComponent(originalPath);
        navigate(decodedPath, { replace: true });
      }
      setHasCheckedPath(true);
    }
  }, [navigate, hasCheckedPath]);

  useEffect(() => {
    monitorNetworkStatus((value) => setIsOnline(value));
  }, []);

  useEffect(() => {
    if (!isIframeMode) {
      TokenManager.setAuthListener(logout);
    }
    return () => {
      TokenManager.removeAuthListener();
    };
  }, [logout, isIframeMode]);

  if (!hasCheckedPath) {
    return null;
  }

  const queryParams = parseQueryParams(location.search);
  const { redirect_path: redirectPath } = queryParams;
  // Post-login: honor an explicit deeplink, else route through the experience
  // picker resolver (DE-336), which decides consumer vs patient and redirects.
  const deepLinkRedirection = redirectPath ? redirectPath : EXPERIENCE_PICKER_PATH;

  if (!isOnline) {
    return <ErrorLoadingPage errorType='offline' hideNavBar />
  }

  if (isProtected && !userData && !isIframeMode) {
    return <Navigate to={LOGIN_PATH} state={{ from: location }} replace />;
  } else if (fromRegistration && userData) {
    return children;
  } else if (!isProtected && userData) {
    return <Navigate to={deepLinkRedirection} replace />;
  } if (isCancerDetectClicked && !patientOrg && location.pathname === '/') {
    // Only the consumer context can be "in CD"; an org context with a stale
    // persisted CD flag (survives reload — CD flag is localStorage, context is
    // sessionStorage) must not bounce to CancerDetect before the switcher effect
    // clears it. See DashboardLayoutContextSwitcher sync effect.
    return <Navigate to={CANCER_DETECT_PATH} />
  }

  return children;
};